import {API_BASE_URL, ENDPOINTS} from "@/api/endpoints.js";
import {getVisitorId} from "@/utils/visitorId.js";

const fetchJson = async (endpoint, options = {}) => {
    // 1. On construit l'URL
    const url = `${API_BASE_URL}${endpoint}`;

    // 2. On prépare les options par défaut
    const defaultHeaders = {
        'Content-Type': 'application/json',
    };

    // 3. L'APPEL FETCH (Avec credentials FORCÉS)
    const res = await fetch(url, {
        ...options,
        headers: {
            ...defaultHeaders,
            ...options.headers
        },
        credentials: 'include'
    });

    // 4. GESTION D'ERREUR ROBUSTE (JSON ou Texte)
    if (!res.ok) {
        const errorBody = await res.text();
        try {
            const errorJson = JSON.parse(errorBody);
            throw new Error(errorJson.message || `Erreur ${res.status}`);
        } catch {
            // Si le parse échoue, c'est que c'était du texte brut ou vide
            throw new Error(errorBody || `Erreur API ${res.status}`);
        }
    }

    // 5. GESTION DE LA RÉPONSE (JSON ou Texte pour le Token)
    const text = await res.text();
    try {
        return text ? JSON.parse(text) : {}; // On tente le JSON
    } catch {
        return text;
    }
};

// --- LES APPELS ---

export const getFeaturedContestBySlug = (slug, contestId = null) => {
    return fetchJson(ENDPOINTS.PUBLIC.GET_FEATURED_CONTEST(slug, contestId));
};

export const getParticipants = (contestId) => {
    return fetchJson(ENDPOINTS.PARTICIPANTS.GET_LEADERBOARD(contestId));
};

// Classement dédié des tirs au but (uniquement les joueurs de la séance, avec leurs pastilles).
export const getPenaltyLeaderboard = (contestId) => {
    return fetchJson(ENDPOINTS.PARTICIPANTS.GET_PENALTY_LEADERBOARD(contestId));
};

export const joinContest = (contestType, contestId, data) => {
    const payload = {
        pseudo: data.pseudo,
        email: data.email,
        source: data.source || null,
        // Optional referral code from the invitation link; a bad/expired code never fails the sign-up.
        referralCode: data.referralCode || null,
        captcha_token: data.captchaToken,
    };

    if (contestType === 'RUGBY') {
        payload.winningTeamId = data.winningTeamId;
        payload.predictionMargin = data.predictionMargin;

        // Prono mi-temps optionnel (opt-in) : envoyé seulement si le joueur a joué la pause
        // (marge HT non nulle, 0 = nul prédit).
        if (data.halfTimeMargin != null) {
            payload.halfTimeWinningTeamId = data.halfTimeWinningTeamId ?? null;
            payload.halfTimeMargin = data.halfTimeMargin;
        }
    } else {
        payload.homeScore = data.team1;
        payload.awayScore = data.team2;

        // Prono mi-temps optionnel (opt-in) : envoyé seulement si le joueur a joué la pause.
        if (data.team1HalfTime != null && data.team2HalfTime != null) {
            payload.homeScoreHalfTime = data.team1HalfTime;
            payload.awayScoreHalfTime = data.team2HalfTime;
        }
    }

    return fetchJson(ENDPOINTS.PUBLIC.REGISTER(contestId), {
        method: 'POST',
        body: JSON.stringify(payload)
    });
};

// Join pénos : inscription d'un latecomer pendant la séance (pseudo + email + captcha, sans prono FT).
// referralCode : un filleul qui arrive pendant la séance reste un inscrit parrainé (la modale l'a promis).
export const joinPenalty = (contestId, data) =>
    fetchJson(ENDPOINTS.PUBLIC.JOIN_PENALTY(contestId), {
        method: 'POST',
        body: JSON.stringify({
            pseudo: data.pseudo,
            email: data.email,
            source: data.source || null,
            referralCode: data.referralCode || null,
            captcha_token: data.captchaToken,
        })
    });

// Rejeu mi-temps : ajoute le prono de la pause à une participation déjà inscrite.
// payload déjà au format API (football : {homeScore, awayScore} ; rugby : {winningTeamId, predictionMargin}).
export const submitHalfTimePrediction = (contestId, payload) =>
    fetchJson(ENDPOINTS.PARTICIPANTS.SUBMIT_HALF_TIME(contestId), {
        method: 'POST',
        body: JSON.stringify(payload)
    });

// Joue un tir au but : enregistre la prédiction (BUT/RATÉ) sur le tir actuellement ouvert.
// Le serveur horodate la réception (anti-triche) ; le 1er prono sur un tir est verrouillé.
export const submitPenaltyPrediction = (contestId, kickId, prediction) =>
    fetchJson(ENDPOINTS.PARTICIPANTS.SUBMIT_PENALTY(contestId), {
        method: 'POST',
        body: JSON.stringify({kickId, prediction})
    });

export const getMyState = async (contestId) => {
    try {
        const result = await fetchJson(ENDPOINTS.PARTICIPANTS.GET_ME(contestId));
        return {data: result, error: null};
    } catch (err) {
        return {data: null, error: err.message};
    }
};

export const trackEvent = (contestId, sourceCode, type) => {

    if (!contestId || contestId === "undefined") {
        return;
    }

    const queryParams = new URLSearchParams({
        src: sourceCode,
        type: type
    });

    // Identifiant visiteur (généré + persisté de façon synchrone AVANT le fetch).
    // Absent si le storage est bloqué → le back retombe sur le fingerprint IP+UA.
    const vid = getVisitorId();
    if (vid) queryParams.set('vid', vid);

    const endpoint = `${ENDPOINTS.PUBLIC.TRACK_EVENT(contestId)}?${queryParams.toString()}`;

    return fetchJson(endpoint, {method: 'POST'}).catch((err) => {
        console.warn(`Tracking failed for ${type}:`, err);
    });
};

export const subscribeMissedLead = (email, contestId, slug) => {
    return fetchJson(ENDPOINTS.PUBLIC.SUBSCRIBE_LEAD, {
        method: 'POST',
        body: JSON.stringify({
            email: email,
            slug: slug,
            contestId: contestId ? Number(contestId) : null
        })
    });
};

export const loginWithMagicToken = (token) => {
    return fetchJson(ENDPOINTS.PUBLIC.AUTH_MAGIC_LOGIN, {
        method: 'POST',
        body: JSON.stringify({token})
    });
};

export const getOrganization = (slug) => {
    return fetchJson(ENDPOINTS.PUBLIC.GET_ORGANIZATION(slug));
};

// Resolves a referral code (from ?ref=) for the welcome screen. Always returns a body (never 404) so the
// caller can degrade to the normal welcome; on any network error we treat it as "no referral".
export const getReferral = async (code) => {
    try {
        return await fetchJson(ENDPOINTS.PUBLIC.GET_REFERRAL(code));
    } catch {
        return null;
    }
};